/**
 * Measurements Query Hooks
 * React Query hooks for v2 measurements and polygon data fetching
 */

import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { measurementsService } from 'src/api/services';
import { queryKeys } from './queryKeys';
import type { CabinetHistoryEntry, InterpolatedData } from 'src/types';

/**
 * Normalize interpolated_data from the backend into the format expected by
 * useVisualizationScales (flat grid_values array + dimensions + domain).
 *
 * The pipeline service (current version) already sends this format directly.
 * Older data in the DB may use a legacy format with a 2D `grid` array of
 * {source, parsedValue} objects. This function handles both.
 */
function normalizeInterpolatedData(raw: any): InterpolatedData | null {
  if (!raw || typeof raw !== 'object') {
    console.warn('[normalizeInterpolatedData] No interpolated_data in response:', raw);
    return null;
  }

  // New pipeline format — already has grid_values
  if (Array.isArray(raw.grid_values) && raw.grid_values.length > 0) {
    console.debug(
      '[normalizeInterpolatedData] Using new pipeline format:',
      `${raw.grid_nx}×${raw.grid_ny}, method=${raw.interpolation_method}`
    );
    return raw as InterpolatedData;
  }

  // Legacy format — 2D grid array (rows of numbers or {source, parsedValue} objects)
  if (Array.isArray(raw.grid) && raw.grid.length > 0) {
    const grid2D: any[][] = raw.grid;
    const gridNy = grid2D.length;
    const gridNx = grid2D[0]?.length ?? 0;
    if (gridNx === 0) return null;

    // Flatten row-major, extracting numeric value from each cell
    const gridValues: number[] = [];
    for (let j = 0; j < gridNy; j++) {
      for (let i = 0; i < gridNx; i++) {
        const cell = grid2D[j]?.[i];
        if (typeof cell === 'number') {
          gridValues.push(cell);
        } else if (cell && typeof cell === 'object' && 'parsedValue' in cell) {
          gridValues.push(Number(cell.parsedValue) || 0);
        } else {
          gridValues.push(Number(cell) || 0);
        }
      }
    }

    // Domain bounds: use metadata if available, otherwise we can't determine them
    // and the hook will fall back to client-side computation
    const metadata = raw.metadata;
    const xDomain = metadata?.x_domain;
    const yDomain = metadata?.y_domain;
    if (!xDomain || !yDomain) {
      console.warn(
        '[normalizeInterpolatedData] Legacy format but missing domain in metadata:',
        metadata
      );
      return null;
    }

    console.debug(
      '[normalizeInterpolatedData] Converted legacy 2D grid format:',
      `${gridNx}×${gridNy}`
    );
    return {
      grid_values: gridValues,
      grid_nx: gridNx,
      grid_ny: gridNy,
      x_domain: xDomain,
      y_domain: yDomain,
      grid_step: metadata?.grid_step ?? 1.0,
      interpolation_method: metadata?.interpolation_method ?? 'unknown',
    };
  }

  console.warn(
    '[normalizeInterpolatedData] Unrecognized format — no grid_values or grid array. Keys:',
    Object.keys(raw)
  );
  return null;
}

/**
 * Normalize one raw snapshot into the shape the visualization hooks expect:
 * numeric x/y/value with `_value`/`_alert`/`_time` aliases on each point, and a
 * flattened `interpolated_data` grid. Some legacy data stores numeric fields as
 * {source, parsedValue} objects instead of plain numbers — extract those.
 */
function normalizeSnapshot(snap: any) {
  if (!snap) return null;
  const extract = (v: any): number =>
    v && typeof v === 'object' && 'parsedValue' in v ? Number(v.parsedValue) : Number(v);
  const raw = (snap.raw_measurements || []).map((point: any) => ({
    ...point,
    x: extract(point.x),
    y: extract(point.y),
    value: extract(point.value),
    _value: extract(point.value),
    // Per-contact deviation features (null on pre-feature snapshots).
    value_dev: point.value_dev != null ? extract(point.value_dev) : null,
    value_z: point.value_z != null ? extract(point.value_z) : null,
    _alert: point.alert,
    _time: point.timestamp,
    // Carried-forward data of a dead cabinet — rendered dimmed.
    _stale: point.stale === true,
    mple: point.mple != null ? extract(point.mple) : undefined,
    abgriff: point.abgriff != null ? extract(point.abgriff) : undefined,
    karte: point.karte != null ? extract(point.karte) : undefined,
    ain: point.ain != null ? extract(point.ain) : undefined,
    cable: point.cable != null ? String(point.cable) : undefined,
    schaltschranknummer:
      point.schaltschranknummer != null ? String(point.schaltschranknummer) : undefined,
  }));
  return {
    ...snap,
    raw_measurements: raw,
    interpolated_data: normalizeInterpolatedData(snap.interpolated_data),
    interpolated_data_dev: snap.interpolated_data_dev
      ? normalizeInterpolatedData(snap.interpolated_data_dev)
      : null,
  };
}

/**
 * Hook to fetch a SINGLE measurement snapshot for an installation, normalized
 * for the viz. Returns the newest snapshot, or — when `at` is given — the
 * snapshot at that exact timestamp (for click-to-navigate on the temporal
 * plot). This replaces fetching the whole history just to display one frame:
 * the Status page only ever renders one snapshot at a time.
 *
 * Polls every VITE_POLLING_INTERVAL ms (only when showing the latest, i.e. no
 * `at` pin) so newly arrived snapshots show up.
 */
export function useLatestSnapshotQuery(installationId: string | null, at: string | null = null) {
  const pollingInterval = Number(import.meta.env.VITE_POLLING_INTERVAL) || 300000;

  return useQuery({
    queryKey: queryKeys.measurements.snapshot(installationId || '', at),
    queryFn: async () => {
      if (!installationId) return null;
      const snap = await measurementsService.getLatest(installationId, at);
      return normalizeSnapshot(snap);
    },
    enabled: !!installationId,
    staleTime: 2 * 60 * 1000,
    // Keep the previously-shown frame visible while fetching a newly-selected
    // timestamp, so the timeline stays put and the map can show a local loading
    // overlay instead of the whole page blanking. `isFetching` flags the swap.
    placeholderData: keepPreviousData,
    // Only poll the live (latest) frame; a pinned past frame never changes.
    refetchInterval: at ? false : pollingInterval,
    refetchIntervalInBackground: true,
  });
}

/**
 * Hook to fetch the last N measurement snapshots (newest first) for an
 * installation, each normalized for the viz. Prefer `useLatestSnapshotQuery`
 * for the Status page (it needs one frame at a time); use this only when you
 * genuinely need many snapshots' grids at once.
 *
 * Polls every VITE_POLLING_INTERVAL ms so newly arrived snapshots show up.
 */
export function useMeasurementHistoryQuery(installationId: string | null, limit: number = 10) {
  const pollingInterval = Number(import.meta.env.VITE_POLLING_INTERVAL) || 300000;

  return useQuery({
    queryKey: queryKeys.measurements.history(installationId || '', limit),
    queryFn: async () => {
      if (!installationId) return [];
      const snapshots = await measurementsService.getHistory(installationId, limit);
      return (snapshots || []).map(normalizeSnapshot);
    },
    enabled: !!installationId,
    staleTime: 2 * 60 * 1000,
    refetchInterval: pollingInterval,
    refetchIntervalInBackground: true,
  });
}

/**
 * Hook to fetch per-cabinet measurement times for the last N snapshots
 * (newest first). Lightweight by design — the endpoint returns only
 * `measurement_timestamp` + `cabinet_status` per snapshot, so the Dashboard's
 * history table costs kilobytes rather than the megabytes `useMeasurementHistoryQuery`
 * would pull just to read timestamps.
 */
export function useCabinetHistoryQuery(
  installationId: string | null,
  limit: number = 5,
  enabled: boolean = true
) {
  const pollingInterval = Number(import.meta.env.VITE_POLLING_INTERVAL) || 300000;

  return useQuery<CabinetHistoryEntry[]>({
    queryKey: queryKeys.measurements.cabinetHistory(installationId || '', limit),
    queryFn: async () => {
      if (!installationId) return [];
      return (await measurementsService.getCabinetHistory(installationId, limit)) || [];
    },
    // Only fetched once the user opens the table — it is collapsed by default.
    enabled: !!installationId && enabled,
    staleTime: 2 * 60 * 1000,
    refetchInterval: pollingInterval,
  });
}

/**
 * Hook to fetch polygons for an installation.
 * Reads structure_polygons from the installation record.
 * Polygons rarely change, so we cache them longer.
 */
export function usePolygonsQuery(installationId: string | null) {
  return useQuery({
    queryKey: queryKeys.polygons.byInstallation(installationId || ''),
    queryFn: async () => {
      if (!installationId) return [];
      return await measurementsService.getPolygons(installationId);
    },
    enabled: !!installationId,
    staleTime: 30 * 60 * 1000,
    // Keep the previous installation's polygons during a switch so they don't
    // blink to empty before the snapshot catches up — otherwise the viz draws a
    // mismatched frame (new polygons over old points, or vice versa). The Status
    // page gates the draw on snapshot/polygons belonging to the same install.
    placeholderData: keepPreviousData,
  });
}
